import { api, decodeVaultBlobResponse, type EmailNotificationType } from '../api/client';

import { getAppState } from '../app/index';

import {
  buildSettingsUrl,
  parseSettingsUrl,
  readSettingsUrlState,
  type SettingsSection,
} from '../app/navigationState';

import { changeVaultPassphrase } from '../crypto/passphraseFlow';

import { createAndPublishRecoveryPackage } from '../crypto/recoveryFlow';

import { isForcePassphraseChangeRequired, setForcePassphraseChange } from '../crypto/passphraseAge';

import { resolvePasswordPolicy } from '../crypto/passwordPoliciesApi';

import { defaultPolicyForTarget } from '../crypto/passwordPolicy';

import { loadVaultSession } from '../crypto/vaultSession';

import { unlockVaultWithPassphrase } from '../crypto/vault';

import {
  enrollDeviceRemember,
  hasDeviceRememberEnrollment,
  isDeviceRememberSupported,
  removeDeviceRememberEnrollment,
} from '../crypto/webauthnDevice';

import { applyI18n, t } from '../i18n';

import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';

import { attachPassphraseWhitespaceWarnings } from '../ui/passphraseWhitespaceWarning';

import {
  bindPasswordPolicyToForm,
  passwordPolicyErrorFromCause,
  validatePasswordForUi,
} from '../ui/passwordPolicyUi';

import { setCryptoBusy } from '../ui/cryptoBusy';

import { showRecoveryExportDialog } from '../ui/recoveryExportDialog';

import { iconImg } from '../ui/icons';

import { mountSectionNav } from '../ui/sectionNav';
import { createHelpTip } from '../help/helpTips';
import { settingsSectionHelpTopic } from '../help/defaultTopic';



export function SettingsPage(): HTMLElement {

  const page = document.createElement('section');

  page.className = 'module-page module-page--settings';
  page.dataset.settingsWorkspace = '';

  const EMAIL_NOTIFICATION_TYPES: EmailNotificationType[] = [
    'infoShareReplyReceived',
    'recoveryPackageCreated',
    'recoveryPackageReplaced',
    'recoveryPackageInvalidated',
    'sharedFolderMemberInvited',
    'sedrMemberInvited',
    'sedrGuestRoomAdded',
    'sedrGuestAccountReady',
    'sedrDeletionRequested',
    'sedrDeletionPending',
    'sharedFolderExpiryDigest',
  ];

  const notificationRowsHtml = EMAIL_NOTIFICATION_TYPES.map((type) => `
      <label class="checkbox-row settings-notification-row">
        <input type="checkbox" name="notification-${type}" data-notification-type="${type}" checked />
        <span>
          <span class="settings-notification-row__label" data-i18n="settings.notifications.types.${type}.label"></span>
          <span class="muted settings-notification-row__description" data-i18n="settings.notifications.types.${type}.description"></span>
        </span>
      </label>`).join('');

  page.innerHTML = `

    <section class="module-section" id="settings-passphrase-section">

      <div class="module-section__header">

        <div class="module-section__header-main">

          <h2 data-i18n="settings.passphrase.title"></h2>

        </div>

      </div>

      <p class="muted" data-i18n="settings.passphrase.subtitle"></p>

      <form class="form-grid settings-passphrase-form" id="settings-passphrase-form" method="post" action="#">

        <label>

          <span data-i18n="settings.passphrase.current"></span>

          <input type="password" name="current" required autocomplete="current-password" />

        </label>

        <label>

          <span data-i18n="settings.passphrase.new"></span>

          <input type="password" name="new" minlength="12" required autocomplete="new-password" />

        </label>

        <label>

          <span data-i18n="settings.passphrase.confirm"></span>

          <input type="password" name="confirm" minlength="12" required autocomplete="new-password" />

        </label>

        <p class="muted" id="settings-passphrase-policy-hint" hidden></p>

        <p class="warning-text" id="settings-passphrase-expired" hidden data-i18n="passwordPolicy.expired"></p>

        <button type="submit" class="button primary" id="settings-passphrase-submit">

          <span data-i18n="settings.passphrase.submit"></span>

        </button>

      </form>

      <p class="success-text" id="settings-passphrase-success" hidden data-i18n="settings.passphrase.success"></p>

      <p class="error-text" id="settings-passphrase-error" hidden></p>

    </section>

    <section class="module-section" id="settings-recovery-section">

      <div class="module-section__header">

        <div class="module-section__header-main">

          <h2 data-i18n="settings.recovery.title"></h2>

        </div>

      </div>

      <p class="muted" id="settings-recovery-status"></p>

      <p class="muted" data-i18n="settings.recovery.subtitle"></p>

      <form class="form-grid settings-recovery-form" id="settings-recovery-form" method="post" action="#">

        <label>

          <span data-i18n="settings.recovery.passphrase"></span>

          <input type="password" name="passphrase" required autocomplete="current-password" />

        </label>

        <button type="submit" class="button primary" id="settings-recovery-submit">

          <span data-i18n="settings.recovery.create"></span>

        </button>

      </form>

      <p class="error-text" id="settings-recovery-error" hidden></p>

    </section>

    <section class="module-section" id="settings-webauthn-section" hidden>

      <div class="module-section__header">

        <div class="module-section__header-main">

          <h2 data-i18n="settings.webauthn.title"></h2>

        </div>

      </div>

      <p class="muted" id="settings-webauthn-status"></p>

      <p class="muted" data-i18n="settings.webauthn.subtitle"></p>

      <div class="form-grid settings-webauthn-actions">

        <button type="button" class="button primary" id="settings-webauthn-enroll" hidden>

          <span class="button__icon"></span>

          <span data-i18n="settings.webauthn.enroll"></span>

        </button>

        <button type="button" class="button" id="settings-webauthn-remove" hidden>

          <span data-i18n="settings.webauthn.remove"></span>

        </button>

      </div>

      <p class="success-text" id="settings-webauthn-success" hidden></p>

      <p class="error-text" id="settings-webauthn-error" hidden></p>

    </section>

    <section class="module-section" id="settings-notifications-section">

      <div class="module-section__header">

        <div class="module-section__header-main">

          <h2 data-i18n="settings.notifications.title"></h2>

        </div>

      </div>

      <p class="muted" data-i18n="settings.notifications.subtitle"></p>

      <div class="form-grid settings-notifications-form" id="settings-notifications-form">
        ${notificationRowsHtml}
      </div>

      <div class="settings-sedr-digests" id="settings-sedr-digests">
        <h3 data-i18n="settings.sedrDigests.title"></h3>
        <div class="settings-sedr-digests__defaults">
          <div class="settings-sedr-digests__defaults-label" data-i18n="settings.sedrDigests.defaultLabel"></div>
          <div class="settings-sedr-digests__toggles">
            <label class="settings-sedr-digests__toggle">
              <input type="checkbox" id="settings-sedr-digest-daily" data-sedr-digest="daily" checked />
              <span data-i18n="settings.sedrDigests.daily.short"></span>
            </label>
            <label class="settings-sedr-digests__toggle">
              <input type="checkbox" id="settings-sedr-digest-biweekly" data-sedr-digest="biweekly" />
              <span data-i18n="settings.sedrDigests.biweekly.short"></span>
            </label>
          </div>
        </div>
        <p class="muted settings-sedr-digests__rule" data-i18n="settings.sedrDigests.rule"></p>
        <div class="settings-sedr-digests__rooms-block">
          <div class="settings-sedr-digests__rooms-head">
            <span data-i18n="settings.sedrDigests.perRoom"></span>
            <span class="settings-sedr-digests__col" data-i18n="settings.sedrDigests.daily.short"></span>
            <span class="settings-sedr-digests__col" data-i18n="settings.sedrDigests.biweekly.short"></span>
            <span class="settings-sedr-digests__col settings-sedr-digests__col--status" aria-hidden="true"></span>
          </div>
          <div id="settings-sedr-digest-rooms" class="settings-sedr-digests__room-list"></div>
        </div>
      </div>

      <p class="error-text" id="settings-notifications-error" hidden></p>

    </section>

  `;

  page.querySelectorAll('.module-section__header-main')[0]?.prepend(iconImg('lock', 22));
  page.querySelectorAll('.module-section__header-main')[1]?.prepend(iconImg('key', 22));

  const webauthnSection = page.querySelector('#settings-webauthn-section') as HTMLElement;
  const passphraseSection = page.querySelector('#settings-passphrase-section') as HTMLElement;
  const recoverySection = page.querySelector('#settings-recovery-section') as HTMLElement;
  const notificationsSection = page.querySelector('#settings-notifications-section') as HTMLElement;
  const sectionNav = mountSectionNav(
    page,
    [
      { id: 'passphrase', labelKey: 'settings.passphrase.title', panel: passphraseSection },
      { id: 'recovery', labelKey: 'settings.recovery.title', panel: recoverySection },
      { id: 'webauthn', labelKey: 'settings.webauthn.title', panel: webauthnSection },
      { id: 'notifications', labelKey: 'settings.notifications.title', panel: notificationsSection },
    ],
    {
      navAriaLabelKey: 'nav.settings',
      buildUrl: (id) => buildSettingsUrl(id as SettingsSection),
      readId: () => readSettingsUrlState()?.section ?? null,
    },
  );

  const pathAfterMount = window.location.pathname.replace(/\/+$/, '') || '/';
  const parsedAfterMount = parseSettingsUrl(pathAfterMount);
  if (parsedAfterMount?.section && sectionNav.getActiveId() !== parsedAfterMount.section) {
    sectionNav.applyFromUrl();
  } else if (pathAfterMount.startsWith('/settings/') && !parsedAfterMount?.section) {
    const active = sectionNav.getActiveId() as SettingsSection;
    history.replaceState(history.state, '', buildSettingsUrl(active || null));
  }

  const onSettingsRoute = () => {
    sectionNav.applyFromUrl();
    syncHelpContext();
  };
  page.addEventListener('settings:route', onSettingsRoute);

  function syncHelpContext(): void {
    page.setAttribute(
      'data-help-context',
      settingsSectionHelpTopic(sectionNav.getActiveId() as SettingsSection),
    );
  }
  syncHelpContext();

  const webauthnStatus = page.querySelector('#settings-webauthn-status') as HTMLParagraphElement;
  const webauthnEnroll = page.querySelector('#settings-webauthn-enroll') as HTMLButtonElement;
  const webauthnRemove = page.querySelector('#settings-webauthn-remove') as HTMLButtonElement;
  const webauthnSuccess = page.querySelector('#settings-webauthn-success') as HTMLParagraphElement;
  const webauthnError = page.querySelector('#settings-webauthn-error') as HTMLParagraphElement;
  const webauthnActions = page.querySelector('.settings-webauthn-actions') as HTMLDivElement;
  const notificationsError = page.querySelector('#settings-notifications-error') as HTMLParagraphElement;
  const notificationsForm = page.querySelector('#settings-notifications-form') as HTMLElement;
  webauthnSection.querySelector('.module-section__header-main')?.prepend(iconImg('fingerprint', 22));
  notificationsSection.querySelector('.module-section__header-main')?.prepend(iconImg('settings', 22));
  webauthnEnroll.querySelector('.button__icon')?.append(iconImg('fingerprint', 18));



  const passphraseForm = page.querySelector('#settings-passphrase-form') as HTMLFormElement;

  const passphraseError = page.querySelector('#settings-passphrase-error') as HTMLParagraphElement;

  const passphraseSuccess = page.querySelector('#settings-passphrase-success') as HTMLParagraphElement;

  const passphrasePolicyHint = page.querySelector('#settings-passphrase-policy-hint') as HTMLElement;

  const passphraseExpired = page.querySelector('#settings-passphrase-expired') as HTMLElement;

  const passphraseNewInput = passphraseForm.querySelector('input[name="new"]') as HTMLInputElement;

  const passphraseConfirmInput = passphraseForm.querySelector('input[name="confirm"]') as HTMLInputElement;

  let passphrasePolicy = defaultPolicyForTarget('vaultPassphrase');

  passphraseExpired.hidden = !isForcePassphraseChangeRequired();

  void resolvePasswordPolicy('vaultPassphrase').then((policy) => {
    passphrasePolicy = policy;
    bindPasswordPolicyToForm({
      policy,
      inputs: [passphraseNewInput, passphraseConfirmInput],
      hint: passphrasePolicyHint,
    });
  });

  attachPassphraseWhitespaceWarnings(
    passphraseForm.querySelector('input[name="current"]') as HTMLInputElement,
    passphraseNewInput,
    passphraseConfirmInput,
  );

  const status = page.querySelector('#settings-recovery-status') as HTMLParagraphElement;

  const recoveryForm = page.querySelector('#settings-recovery-form') as HTMLFormElement;

  attachPassphraseWhitespaceWarnings(
    recoveryForm.querySelector('input[name="passphrase"]') as HTMLInputElement,
  );

  const submitButton = page.querySelector('#settings-recovery-submit') as HTMLButtonElement;

  const submitLabel = submitButton.querySelector('[data-i18n]') as HTMLElement;

  const recoveryError = page.querySelector('#settings-recovery-error') as HTMLParagraphElement;



  let recoveryActive = false;

  let recoveryGeneration = 0;

  let recoveryErrorKey: string | null = null;

  let passphraseErrorKey: string | null = null;

  let webauthnEnrolled = false;

  let webauthnErrorKey: string | null = null;

  let notificationsErrorKey: string | null = null;

  let notificationBusy = false;



  function applyNotificationPreferences(preferences: { type: string; enabled: boolean }[]): void {
    for (const pref of preferences) {
      const input = notificationsForm.querySelector(
        `input[data-notification-type="${pref.type}"]`,
      ) as HTMLInputElement | null;
      if (input) {
        input.checked = pref.enabled;
      }
    }
  }

  const sedrDigestDaily = page.querySelector('#settings-sedr-digest-daily') as HTMLInputElement;
  const sedrDigestBiweekly = page.querySelector('#settings-sedr-digest-biweekly') as HTMLInputElement;
  const sedrDigestRooms = page.querySelector('#settings-sedr-digest-rooms') as HTMLElement;
  const sedrDigestBlock = page.querySelector('#settings-sedr-digests') as HTMLElement;
  let sedrDigestBusy = false;

  function escapeAttr(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function applySedrDigestPreferences(prefs: {
    dailyEnabled: boolean;
    biweeklyEnabled: boolean;
    rooms: { roomId: string; title: string; dailyEnabled: boolean; biweeklyEnabled: boolean }[];
  }): void {
    sedrDigestDaily.checked = prefs.dailyEnabled;
    sedrDigestBiweekly.checked = prefs.biweeklyEnabled;
    sedrDigestDaily.closest('label')?.setAttribute('title', t('settings.sedrDigests.daily.description'));
    sedrDigestBiweekly.closest('label')?.setAttribute('title', t('settings.sedrDigests.biweekly.description'));

    if (prefs.rooms.length === 0) {
      sedrDigestRooms.innerHTML = `<p class="muted settings-sedr-digests__empty" data-i18n="settings.sedrDigests.noRooms"></p>`;
      applyI18n(sedrDigestRooms);
      return;
    }

    sedrDigestRooms.innerHTML = prefs.rooms.map((room) => {
      const dailyCustom = room.dailyEnabled !== prefs.dailyEnabled;
      const biweeklyCustom = room.biweeklyEnabled !== prefs.biweeklyEnabled;
      const isCustom = dailyCustom || biweeklyCustom;
      return `
      <div class="settings-sedr-digests__room-row${isCustom ? ' settings-sedr-digests__room-row--custom' : ''}" data-room-id="${escapeAttr(room.roomId)}">
        <span class="settings-sedr-digests__room-title">${escapeAttr(room.title)}</span>
        <label class="settings-sedr-digests__col">
          <input type="checkbox" data-sedr-digest-room="daily" data-room-id="${escapeAttr(room.roomId)}" ${room.dailyEnabled ? 'checked' : ''} aria-label="${escapeAttr(t('settings.sedrDigests.daily.short'))}" />
        </label>
        <label class="settings-sedr-digests__col">
          <input type="checkbox" data-sedr-digest-room="biweekly" data-room-id="${escapeAttr(room.roomId)}" ${room.biweeklyEnabled ? 'checked' : ''} aria-label="${escapeAttr(t('settings.sedrDigests.biweekly.short'))}" />
        </label>
        <span class="settings-sedr-digests__col settings-sedr-digests__col--status">
          ${isCustom ? `<span class="settings-sedr-digests__badge" data-i18n="settings.sedrDigests.custom"></span>` : ''}
        </span>
      </div>`;
    }).join('');
    applyI18n(sedrDigestRooms);
  }

  function setNotificationsError(key: string | null): void {
    notificationsErrorKey = key;
    if (key) {
      notificationsError.hidden = false;
      notificationsError.textContent = t(key);
    } else {
      notificationsError.hidden = true;
      notificationsError.textContent = '';
    }
  }



  function refreshWebauthnStatus(): void {

    if (webauthnEnrolled) {

      webauthnStatus.textContent = t('settings.webauthn.statusEnrolled');

      webauthnEnroll.hidden = true;

      webauthnRemove.hidden = false;

    } else {

      webauthnStatus.textContent = t('settings.webauthn.statusNotEnrolled');

      webauthnEnroll.hidden = false;

      webauthnRemove.hidden = true;

    }

  }



  function refreshRecoveryStatus(): void {

    if (recoveryActive) {

      status.textContent = t('settings.recovery.statusActive', { generation: recoveryGeneration });

      submitLabel.setAttribute('data-i18n', 'settings.recovery.replace');

    } else {

      status.textContent = t('settings.recovery.statusInactive');

      submitLabel.setAttribute('data-i18n', 'settings.recovery.create');

    }

    applyI18n(submitButton);

  }



  const unbindLocale = bindLocaleRefresh(() => {

    refreshRecoveryStatus();

    if (recoveryErrorKey && !recoveryError.hidden) {

      recoveryError.textContent = t(recoveryErrorKey);

    }

    if (passphraseErrorKey && !passphraseError.hidden) {

      passphraseError.textContent = t(passphraseErrorKey);

    }

    if (webauthnErrorKey && !webauthnError.hidden) {

      webauthnError.textContent = t(webauthnErrorKey);

    }

    if (notificationsErrorKey && !notificationsError.hidden) {

      notificationsError.textContent = t(notificationsErrorKey);

    }

    refreshWebauthnStatus();

  });



  void (async () => {

    try {

      const vaultStatus = await api.getVaultStatus();

      recoveryActive = vaultStatus.recoveryActive;

      recoveryGeneration = vaultStatus.recoveryGeneration;

      refreshRecoveryStatus();

      const state = getAppState();

      if (state.kind === 'authenticated' && (await isDeviceRememberSupported())) {

        sectionNav.setVisible('webauthn', true);

        webauthnEnrolled = await hasDeviceRememberEnrollment(state.session.userId);

        refreshWebauthnStatus();

      }

      try {
        const notificationPrefs = await api.getEmailNotifications();
        applyNotificationPreferences(notificationPrefs.preferences);
        setNotificationsError(null);
      } catch (cause) {
        console.error('Load email notification preferences failed', cause);
        setNotificationsError('settings.notifications.loadError');
      }

      try {
        const digestPrefs = await api.getSedrDigestPreferences();
        applySedrDigestPreferences(digestPrefs);
      } catch (cause) {
        console.error('Load SEDR digest preferences failed', cause);
        setNotificationsError('settings.notifications.loadError');
      }

    } catch (cause) {

      console.error('Load vault status failed', cause);

    }

  })();



  notificationsForm.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox') {
      return;
    }

    const type = target.dataset.notificationType as EmailNotificationType | undefined;
    if (!type || notificationBusy) {
      return;
    }

    const enabled = target.checked;
    notificationBusy = true;
    setNotificationsError(null);

    void (async () => {
      try {
        const updated = await api.putEmailNotifications([{ type, enabled }]);
        applyNotificationPreferences(updated.preferences);
      } catch (cause) {
        console.error('Update email notification preferences failed', cause);
        target.checked = !enabled;
        setNotificationsError('settings.notifications.error');
      } finally {
        notificationBusy = false;
      }
    })();
  });

  sedrDigestBlock.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox' || sedrDigestBusy) {
      return;
    }

    const enabled = target.checked;
    const globalKind = target.dataset.sedrDigest;
    const roomKind = target.dataset.sedrDigestRoom;
    const roomId = target.dataset.roomId;
    sedrDigestBusy = true;
    setNotificationsError(null);

    void (async () => {
      try {
        let updated;
        if (globalKind === 'daily') {
          updated = await api.putSedrDigestPreferences({ dailyEnabled: enabled });
        } else if (globalKind === 'biweekly') {
          updated = await api.putSedrDigestPreferences({ biweeklyEnabled: enabled });
        } else if (roomKind && roomId) {
          updated = await api.putSedrDigestPreferences({
            rooms: [{
              roomId,
              ...(roomKind === 'daily' ? { dailyEnabled: enabled } : { biweeklyEnabled: enabled }),
            }],
          });
        } else {
          return;
        }
        applySedrDigestPreferences(updated);
      } catch (cause) {
        console.error('Update SEDR digest preferences failed', cause);
        target.checked = !enabled;
        setNotificationsError('settings.notifications.error');
      } finally {
        sedrDigestBusy = false;
      }
    })();
  });



  passphraseForm.addEventListener('submit', (event) => {

    event.preventDefault();

    void (async () => {

      passphraseError.hidden = true;

      passphraseSuccess.hidden = true;

      passphraseErrorKey = null;



      const state = getAppState();

      if (state.kind !== 'authenticated') {

        return;

      }



      if (!loadVaultSession()) {

        passphraseErrorKey = 'settings.recovery.locked';

        passphraseError.hidden = false;

        passphraseError.textContent = t(passphraseErrorKey);

        return;

      }



      const data = new FormData(passphraseForm);

      const currentPassphrase = String(data.get('current') ?? '');

      const newPassphrase = String(data.get('new') ?? '');

      const confirmPassphrase = String(data.get('confirm') ?? '');



      if (newPassphrase !== confirmPassphrase) {

        passphraseErrorKey = 'settings.passphrase.mismatch';

        passphraseError.hidden = false;

        passphraseError.textContent = t(passphraseErrorKey);

        return;

      }



      if (currentPassphrase === newPassphrase) {

        passphraseErrorKey = 'settings.passphrase.sameAsCurrent';

        passphraseError.hidden = false;

        passphraseError.textContent = t(passphraseErrorKey);

        return;

      }



      const policyMessage = validatePasswordForUi(newPassphrase, passphrasePolicy);

      if (policyMessage) {

        passphraseErrorKey = null;

        passphraseError.hidden = false;

        passphraseError.textContent = policyMessage;

        return;

      }



      setCryptoBusy(page, passphraseForm, true, {

        titleKey: 'settings.passphrase.working',

        detailKey: 'settings.passphrase.workingDetail',

        icon: 'hourglass',

      });



      try {

        await changeVaultPassphrase(currentPassphrase, newPassphrase, 'vaultPassphrase');

        passphraseForm.reset();

        passphraseSuccess.hidden = false;

        setForcePassphraseChange(false);

        passphraseExpired.hidden = true;

      } catch (cause) {

        console.error('Change passphrase failed', cause);

        const policyError = passwordPolicyErrorFromCause(cause, passphrasePolicy);

        if (policyError) {

          passphraseErrorKey = null;

          passphraseError.hidden = false;

          passphraseError.textContent = policyError;

        } else {

          passphraseErrorKey = cause instanceof Error && cause.message === 'vault_version_mismatch'

            ? 'vault.unlock.versionMismatch'

            : 'settings.passphrase.invalidCurrent';

          passphraseError.hidden = false;

          passphraseError.textContent = t(passphraseErrorKey);

        }

      } finally {

        setCryptoBusy(page, passphraseForm, false, {

          titleKey: 'settings.passphrase.working',

          detailKey: 'settings.passphrase.workingDetail',

        });

      }

    })();

  });



  recoveryForm.addEventListener('submit', (event) => {

    event.preventDefault();

    void (async () => {

      recoveryError.hidden = true;

      recoveryErrorKey = null;



      const state = getAppState();

      if (state.kind !== 'authenticated') {

        return;

      }



      const session = loadVaultSession();

      if (!session) {

        recoveryErrorKey = 'settings.recovery.locked';

        recoveryError.hidden = false;

        recoveryError.textContent = t(recoveryErrorKey);

        return;

      }



      const passphrase = String(new FormData(recoveryForm).get('passphrase') ?? '');

      if (!passphrase) {

        return;

      }



      setCryptoBusy(page, recoveryForm, true, {

        titleKey: 'settings.recovery.working',

        detailKey: 'settings.recovery.workingDetail',

        icon: 'hourglass',

      });



      try {

        const blobResponse = await api.getVaultBlob();

        const blob = decodeVaultBlobResponse(blobResponse);

        await unlockVaultWithPassphrase(

          passphrase,

          blob.vaultSalt,

          blob.vkWrapPassphrase,

          blob.vaultBlob,

          blob.vaultVersion,

        );



        const created = await createAndPublishRecoveryPackage(

          state.session.userId,

          session.vaultKey,

        );



        recoveryActive = true;

        recoveryGeneration = created.recoveryGeneration;

        refreshRecoveryStatus();

        recoveryForm.reset();

        await showRecoveryExportDialog(created.exportText);

      } catch (cause) {

        console.error('Create recovery package failed', cause);

        recoveryErrorKey = cause instanceof Error && cause.message === 'vault_version_mismatch'

          ? 'vault.unlock.versionMismatch'

          : 'status.error';

        recoveryError.hidden = false;

        recoveryError.textContent = t(recoveryErrorKey);

      } finally {

        setCryptoBusy(page, recoveryForm, false, {

          titleKey: 'settings.recovery.working',

          detailKey: 'settings.recovery.workingDetail',

        });

      }

    })();

  });



  webauthnEnroll.addEventListener('click', () => {

    void (async () => {

      webauthnError.hidden = true;

      webauthnSuccess.hidden = true;

      webauthnErrorKey = null;



      const state = getAppState();

      if (state.kind !== 'authenticated') {

        return;

      }



      const session = loadVaultSession();

      if (!session) {

        webauthnErrorKey = 'settings.webauthn.locked';

        webauthnError.hidden = false;

        webauthnError.textContent = t(webauthnErrorKey);

        return;

      }



      setCryptoBusy(page, webauthnActions, true, {

        titleKey: 'settings.webauthn.working',

        detailKey: 'settings.webauthn.workingDetail',

        icon: 'fingerprint',

      });



      try {

        await enrollDeviceRemember(state.session.userId, session.vaultKey);

        webauthnEnrolled = true;

        refreshWebauthnStatus();

        webauthnSuccess.hidden = false;

        webauthnSuccess.textContent = t('settings.webauthn.enrolled');

      } catch (cause) {

        console.error('WebAuthn enrollment failed', cause);

        webauthnErrorKey = cause instanceof Error && cause.message === 'prf_not_supported'
          ? 'settings.webauthn.unavailable'
          : cause instanceof Error && cause.message === 'webauthn_cancelled'
            ? 'settings.webauthn.cancelled'
            : 'status.error';

        webauthnError.hidden = false;

        webauthnError.textContent = t(webauthnErrorKey);

      } finally {

        setCryptoBusy(page, webauthnActions, false, {

          titleKey: 'settings.webauthn.working',

          detailKey: 'settings.webauthn.workingDetail',

        });

      }

    })();

  });



  webauthnRemove.addEventListener('click', () => {

    void (async () => {

      webauthnError.hidden = true;

      webauthnSuccess.hidden = true;

      webauthnErrorKey = null;



      const state = getAppState();

      if (state.kind !== 'authenticated') {

        return;

      }



      try {

        await removeDeviceRememberEnrollment(state.session.userId);

        webauthnEnrolled = false;

        refreshWebauthnStatus();

        webauthnSuccess.hidden = false;

        webauthnSuccess.textContent = t('settings.webauthn.removed');

      } catch (cause) {

        console.error('WebAuthn removal failed', cause);

        webauthnErrorKey = 'status.error';

        webauthnError.hidden = false;

        webauthnError.textContent = t(webauthnErrorKey);

      }

    })();

  });



  const observer = new MutationObserver(() => {

    if (!page.isConnected) {

      unbindLocale();
      page.removeEventListener('settings:route', onSettingsRoute);

      observer.disconnect();

    }

  });

  observer.observe(document.body, { childList: true, subtree: true });



  applyI18n(page);
  page.querySelector('#settings-passphrase-section .module-section__header-main h2')?.append(createHelpTip('settings-passphrase'));
  page.querySelector('#settings-recovery-section .module-section__header-main h2')?.append(createHelpTip('settings-recovery'));
  page.querySelector('#settings-webauthn-section .module-section__header-main h2')?.append(createHelpTip('settings-passkey'));
  page.querySelector('#settings-notifications-section .module-section__header-main h2')?.append(createHelpTip('settings-notifications'));

  return page;

}


